import React from 'react';
import { GamePhase, CardColor } from '../types';
import { Language, t } from '../data/translations';
import { ElementWaveSignal } from './WaveSignal';
import { LayoutContext } from '../contexts/LayoutContext';

interface HUDProps {
  phase: GamePhase;
  isMyTurn: boolean;
  onEndDay: () => void;
  onPass: () => void;
  endDayDisabled: boolean;
  passDisabled: boolean;
  language: Language;
  // Action hint props
  endDayHint?: { isAIBest: boolean };
  passHint?: { isAIBest: boolean };
}

const HUD: React.FC<HUDProps> = ({
  phase,
  isMyTurn,
  onEndDay,
  onPass,
  endDayDisabled,
  passDisabled,
  language,
  endDayHint,
  passHint
}) => {
  // Animation sheet uses: width = 100vh * (480/847), right = calc(11rem - 40px)
  // To center button horizontally over the animation sheet:
  // Sheet center from right = (11rem - 40px) + (100vh * 240 / 847)
  // Button width = 9.6rem, so button center offset = 4.8rem
  // Final: right = (11rem - 40px) + (100vh * 240/847) - 4.8rem = 6.2rem - 40px + (100vh * 240/847)
  const aspectRatio = 480 / 847;
  // Subtract 23px for 23px right shift, add 10px to bottom/top for 10px down shift
  const rightPos = `calc(6.2rem - 40px + (100vh * ${aspectRatio / 2}) - 23px)`;

  const posClass = (phase === GamePhase.Combat || phase === GamePhase.TowerPurchase)
    ? (isMyTurn ? 'bottom-[14px]' : 'top-[34px] invisible')
    : 'bottom-[14px]';

  const layout = React.useContext(LayoutContext);
  const isMobile = layout?.isMobileShell;

  if (isMobile) {
    // On mobile, position floating right above the hand scroller
    // Phase-aware transition: NIGHT (bottom-right) vs DAY (custom)
    const mobilePosClass = (phase === GamePhase.Combat || phase === GamePhase.TowerPurchase)
      ? (isMyTurn ? 'bottom-[6rem] right-[1rem] translate-x-0' : 'bottom-[6rem] right-[1rem] translate-x-[200%] opacity-0')
      : 'bottom-[6rem] right-[1rem] translate-x-0';

    return (
      <div className={`absolute z-[55] transition-all duration-500 pointer-events-auto ${mobilePosClass}`}>
        {phase === GamePhase.Preparation && (
          <div className="relative w-[5.5rem] h-[5.5rem]">
            <button id="btn-end-day" onClick={onEndDay} disabled={endDayDisabled} className="w-[5.5rem] h-[5.5rem] text-white font-bold rounded-full flex items-center justify-center text-center leading-tight transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95 bg-slate-800 border-2 border-slate-500 shadow-xl">
              <span className="text-[0.9rem] leading-[1rem] tracking-tight">{t('ui.endDay', language)}</span>
            </button>
            {endDayHint && !endDayDisabled && (
              <div className="absolute inset-0 pointer-events-none scale-90">
                <ElementWaveSignal
                  isAIBest={endDayHint.isAIBest}
                  shape="CIRCLE"
                />
              </div>
            )}
          </div>
        )}
        {(phase === GamePhase.Combat || phase === GamePhase.TowerPurchase) && (
          <div className="relative w-[5.5rem] h-[5.5rem]">
            <button id="btn-pass" onClick={onPass} disabled={passDisabled} className="w-[5.5rem] h-[5.5rem] text-white font-bold rounded-full flex items-center justify-center text-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95 bg-slate-800 border-2 border-slate-500 shadow-xl">
              <span className="text-xl tracking-tight">{t('ui.pass', language)}</span>
            </button>
            {passHint && !passDisabled && (
              <div className="absolute inset-0 pointer-events-none scale-90">
                <ElementWaveSignal
                  isAIBest={passHint.isAIBest}
                  shape="CIRCLE"
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`fixed z-[35] transition-all duration-500 ${posClass}`} style={{ right: rightPos }}>
      {phase === GamePhase.Preparation && (
        <div className="relative w-[9.6rem] h-[9.6rem]">
          <button id="btn-end-day" onClick={onEndDay} disabled={endDayDisabled} className="w-[9.6rem] h-[9.6rem] text-white font-bold rounded-full flex items-center justify-center text-center leading-tight transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105">
            <span className="text-2xl" style={{ textShadow: '0 0 2px #0b132b, 1px 1px 0 #0b132b, -1px -1px 0 #0b132b', WebkitTextStroke: '1px #0b132b', transform: 'translate(-10px, -10px)' }}>{t('ui.endDay', language)}</span>
          </button>
          {endDayHint && !endDayDisabled && (
            <div className="absolute inset-0 pointer-events-none" style={{ transform: 'translate(-10px, -10px)' }}>
              <ElementWaveSignal
                isAIBest={endDayHint.isAIBest}
                shape="CIRCLE"
              />
            </div>
          )}
        </div>
      )}
      {(phase === GamePhase.Combat || phase === GamePhase.TowerPurchase) && (
        <div className="relative w-[9.6rem] h-[9.6rem]">
          <button id="btn-pass" onClick={onPass} disabled={passDisabled} className="w-[9.6rem] h-[9.6rem] text-white font-bold rounded-full flex items-center justify-center text-center transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105">
            <span className="text-2xl" style={{ textShadow: '0 0 2px #0b132b, 1px 1px 0 #0b132b, -1px -1px 0 #0b132b', WebkitTextStroke: '1px #0b132b', transform: 'translate(-10px, -10px)' }}>{t('ui.pass', language)}</span>
          </button>
          {passHint && !passDisabled && (
            <div className="absolute inset-0 pointer-events-none" style={{ transform: 'translate(-10px, -10px)' }}>
              <ElementWaveSignal
                isAIBest={passHint.isAIBest}
                shape="CIRCLE"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default HUD
